"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import AddToCart from "@components/AddToCart";
import TrackedLink from "@components/TrackedLink";
import { track } from "@lib/track";
import { Lock, X } from "lucide-react";
import { schemaBrand, schemaOfferPolicies } from "@lib/productSchema";

// ---------------- Types ----------------
interface Product {
  id: string;
  imageSrc: string;
  title: string;
  price: string;
  description: string;
  slug: string;
  badge?: string;
  tagline?: string;
  rating?: number;
  reviewCount?: number;
  trust?: { label: string; icon?: React.ReactNode }[];
  // Same string|array convention as `who` and `ingredients`: an array renders as
  // separate paragraphs, for directions that cover more than one use. Bentonite
  // Clay (bath + mask + handling warning) needed it until it left the range.
  directions?: string[] | string;
  // Points at the sibling product that answers the OTHER use. Added when GSC
  // showed /shop/bentonite-clay (external only) absorbing internal-use queries
  // that /shop/magic-clay answers; that product has since been discontinued.
  crossSell?: { slug: string; heading: string; body: string };
  ingredients?: string[] | string;
  size?: string;
  faq?: { question: string; answer: string }[];
  blog?: string;
  who?: string[] | string;
  perDay?: string;
}

type AddToCartDetail = { qty: number; product: Product };

// ---------------- UI bits ----------------
const Stars: React.FC<{ rating: number }> = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span aria-label={`${rating} out of 5 stars`} className="text-sm leading-none text-amber-500">
      {"★".repeat(full)}
      {half ? "☆" : ""}
      {"✩".repeat(empty)}
    </span>
  );
};

/** Minimal, monochrome payment badges (inline SVGs) */
const PaymentsRow: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className={`flex items-center flex-wrap gap-2 ${compact ? "text-[11px]" : "text-xs"} text-gray-600`}>
    {/* VISA */}
    <span className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-accent)] bg-[var(--color-bg-muted)] px-2.5 py-1">
      <svg width="22" height="12" viewBox="0 0 40 24" aria-hidden className="opacity-80">
        <text x="4" y="16" fontFamily="ui-sans-serif,system-ui" fontSize="14" fontWeight="700">VISA</text>
      </svg>
      <span className="sr-only">VISA</span>
    </span>
    {/* Mastercard */}
    <span className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-accent)] bg-[var(--color-bg-muted)] px-2.5 py-1">
      <svg width="22" height="12" viewBox="0 0 40 24" aria-hidden className="opacity-80">
        <circle cx="14" cy="12" r="8" className="text-gray-700" fill="currentColor" />
        <circle cx="24" cy="12" r="8" className="text-gray-400" fill="currentColor" />
      </svg>
      <span className="sr-only">Mastercard</span>
    </span>
    {/* American Express */}
    <span className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-accent)] bg-[var(--color-bg-muted)] px-2.5 py-1">
      <svg width="28" height="12" viewBox="0 0 48 24" aria-hidden className="opacity-80">
        <text x="2" y="16" fontFamily="ui-sans-serif,system-ui" fontSize="12" fontWeight="700">AMEX</text>
      </svg>
      <span className="sr-only">American Express</span>
    </span>
    {/* Apple Pay */}
    <span className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-accent)] bg-[var(--color-bg-muted)] px-2.5 py-1">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="opacity-80">
        <path d="M16.365 1.43c-.97.057-2.115.693-2.77 1.515-.604.76-1.105 1.915-.91 3.03 1.06.083 2.15-.61 2.79-1.455.61-.81 1.06-1.967.89-3.09zM20.25 12.8c-.038-3.05 2.493-4.66 2.607-4.73-1.42-2.085-3.63-2.373-4.402-2.398-1.868-.19-3.64 1.1-4.585 1.1-.96 0-2.41-1.077-3.973-1.05-2.037.03-3.925 1.18-4.97 3.005-2.12 3.665-.54 9.1 1.49 12.085.987 1.42 2.155 3.01 3.69 2.952 1.49-.06 2.05-.95 3.85-.95 1.79 0 2.31.95 3.89.92 1.61-.03 2.63-1.44 3.61-2.86 1.14-1.66 1.61-3.27 1.64-3.36-.036-.015-3.16-1.21-3.17-4.617z" />
      </svg>
      <span className="font-medium">Pay</span>
    </span>
    {/* Google Pay */}
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-accent)] bg-[var(--color-bg-muted)] px-2.5 py-1">
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden className="opacity-80">
        <circle cx="12" cy="12" r="10" fill="currentColor" />
      </svg>
      <span className="font-medium">G&nbsp;Pay</span>
    </span>
  </div>
);

// ---------------- Page ----------------
// The product is resolved on the SERVER and handed down. It used to arrive as a
// `slug` that this component looked up in a useEffect, which meant `product` was
// null during render on the server — so every product page's server HTML was this
// component's "Product not found" empty state, with no description, no accordions
// and no Product JSON-LD. Google recovered it on its second, JS-rendering pass;
// social scrapers and AI crawlers never did. Keep the resolution on the server:
// app/shop/[slug]/page.tsx already has the product in hand to build `metadata`.
const ProductDetailClient: React.FC<{ product: Product }> = ({ product }) => {
  const [toast, setToast] = useState<string>("");
  const [y, setY] = useState(0);

  const trackAccordion = (section: string, open: boolean) => {
    try {
      track(
        "accordion_toggle",
        { label: section, component: "ProductDetailPage" },
        {
          event: "accordion_toggle",
          label: section,
          component: "ProductDetailPage",
          meta: {
            open,
            productId: product.id,
            productSlug: product.slug,
            productTitle: product.title,
          },
        }
      );
    } catch {
      /* no-op */
    }
    window.dataLayer?.push({
      event: "accordion_toggle",
      component: "ProductDetailPage",
      section,
      open,
      productId: product.id,
      productSlug: product.slug,
      productTitle: product.title,
    });
  };

  // Custom event: add-to-cart
  useEffect(() => {
    const onAdd = (e: CustomEvent<AddToCartDetail>) => {
      const { qty, product: prod } = e.detail;
      setToast(`${qty} × ${prod.title} added to cart`);
      setTimeout(() => setToast(""), 2200);
    };
    window.addEventListener("add-to-cart", onAdd as EventListener);
    return () => window.removeEventListener("add-to-cart", onAdd as EventListener);
  }, []);

  // Parallax
  useEffect(() => {
    const onScroll = () => setY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hasRating = typeof product.rating === "number" && typeof product.reviewCount === "number";

  const badgeStyles: Record<string, string> = {
    "Best Seller": "bg-amber-500 text-white rounded-full",
    "Top Pick": "bg-green-700 text-white rounded-lg",
    "Limited Batch": "bg-teal-500 text-white rounded-md",
    "100% Natural": "bg-emerald-700 text-white rounded-xl",
  };

  // Safe numeric price for JSON-LD — must be a number, not a string
  const numericPrice = parseFloat(product.price.replace(/[^\d.]/g, ""));
  const displayPrice = product.price.replace(/\s*GBP\b/gi, "").trim();
  const shippingGap = Math.ceil(40 - numericPrice);

  return (
    <section className="relative min-h-screen bg-gradient-to-b from-white to-amber-50/20">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-2 rounded-xl shadow-lg">
          <span>{toast}</span>
          <button
            onClick={() => setToast("")}
            aria-label="Dismiss notification"
            className="text-white/70 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header / Breadcrumb */}
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-6 md:py-10">
        <nav className="text-sm text-gray-600">
          <TrackedLink
            href="/shop"
            variant="text"
            className="hover:underline"
            event="nav_click"
            label="breadcrumb_shop"
            component="ProductDetailPage"
          >
            Shop
          </TrackedLink>
          <span className="mx-2">/</span>
          <span className="text-[var(--color-text-brand)]">{product.title}</span>
        </nav>
      </div>

      {/* Main grid */}
      <div className="mx-auto max-w-6xl px-4 md:px-8 pb-16 grid md:grid-cols-2 gap-10 items-start">
        {/* Media */}
        <div
          className="relative rounded-3xl overflow-hidden shadow-lg bg-[var(--color-bg-muted)]/70 backdrop-blur-sm aspect-[4/3]"
          style={{ transform: `translateY(${y * 0.02}px)` }}
        >
          {product.badge && (
            <span
              className={`absolute top-4 left-4 z-10 text-xs px-3 py-1 font-semibold tracking-wide shadow ${
                badgeStyles[product.badge] || "bg-[var(--color-text-brand)] text-white rounded-md"
              }`}
            >
              {product.badge}
            </span>
          )}
          <Image
            src={product.imageSrc}
            alt={product.title}
            fill
            className="object-cover select-none transition-transform duration-500 hover:scale-[1.03]"
            priority
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>

        {/* Details / Buy Box */}
        <div className="flex flex-col">
          <h1 className="text-4xl md:text-5xl text-[var(--color-text-brand)] font-extrabold tracking-tight">{product.title}</h1>

          {product.tagline && (
            <p className="mt-3 text-lg text-gray-600 max-w-prose">{product.tagline}</p>
          )}

          {hasRating && (
            <div className="mt-3 flex items-center gap-2 text-gray-700">
              <Stars rating={product.rating!} />
              <span className="text-sm">
                {product.rating!.toFixed(1)} · {product.reviewCount} reviews
              </span>
            </div>
          )}

          {/* Premium Buy Card */}
          <div className="mt-7 bg-[var(--color-bg-muted)]/80 backdrop-blur-md border border-[var(--color-accent)] rounded-2xl p-6 shadow-sm">
            {product.badge && (
              <div className="mb-3">
                <span className={`text-xs font-semibold px-2.5 py-1 inline-block ${badgeStyles[product.badge] || "bg-[var(--color-text-brand)] text-white rounded-md"}`}>
                  {product.badge}
                </span>
              </div>
            )}

            <p className="text-2xl font-semibold text-[var(--color-brand)]">{displayPrice}</p>
            {product.perDay && (
              <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{product.perDay}</p>
            )}
            {shippingGap > 0 ? (
              <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                Add <span className="font-semibold">£{shippingGap}</span> more for{" "}
                <TrackedLink
                  href="/shop"
                  variant="text"
                  size="inline"
                  className="text-xs underline-offset-2"
                  event="nav_click"
                  label="free_shipping_nudge"
                  component="ProductDetailPage"
                >
                  free UK shipping
                </TrackedLink>
              </p>
            ) : (
              <p className="text-xs text-[var(--color-text-secondary)] mt-1">Qualifies for free UK shipping</p>
            )}

            <div className="mt-5">
              <AddToCart product={product} useStepper layout="block" />
            </div>

            {/* Key info pills */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {product.size && (
                <div className="rounded-xl border border-[var(--color-accent)] p-3 text-center">
                  <p className="text-xs text-[var(--color-text-secondary)]">Pack Size</p>
                  <p className="font-medium text-[var(--color-text-brand)]">{product.size}</p>
                </div>
              )}
              <div className="rounded-xl border border-[var(--color-accent)] p-3 text-center">
                <p className="text-xs text-[var(--color-text-secondary)]">Dispatch</p>
                <p className="font-medium text-[var(--color-text-brand)]">Ships in 24–48h</p>
              </div>
              <div className="rounded-xl border border-[var(--color-accent)] p-3 text-center">
                <p className="text-xs text-[var(--color-text-secondary)]">Returns</p>
                <p className="font-medium text-[var(--color-text-brand)]">30-day guarantee</p>
              </div>
            </div>
          </div>

          {/* Description — always visible, no accordion */}
          <div className="mt-8 bg-[var(--color-bg-muted)] rounded-xl border border-[var(--color-accent)] p-5">
            <h2 className="font-semibold text-[var(--color-text-brand)] mb-3">About this product</h2>
            <p className="text-gray-700 leading-relaxed">{product.description}</p>
          </div>

          {/* Cross-sell — the two clay products answer different questions and the
              search data shows the wrong one fielding both. Sits directly under the
              description so someone who arrived on an internal-use query sees it
              before the fold's worth of scrolling. */}
          {product.crossSell && (
            <div className="mt-3 rounded-xl border border-[var(--color-accent)] bg-white p-5">
              <h2 className="font-semibold text-[var(--color-text-brand)] mb-2">
                {product.crossSell.heading}
              </h2>
              <p className="text-gray-700 leading-relaxed">{product.crossSell.body}</p>
              <TrackedLink
                href={`/shop/${product.crossSell.slug}`}
                variant="text"
                className="mt-3 inline-block font-medium text-[var(--color-text-brand)] underline"
                event="cta_click"
                label={`crosssell_${product.crossSell.slug}`}
                component="ProductDetailPage"
              >
                View it here
              </TrackedLink>
            </div>
          )}

          {/* Detail accordions */}
          <div className="mt-3 space-y-3">

            {product.who && (
              <details
                open
                className="group bg-[var(--color-bg-muted)] rounded-xl border border-[var(--color-accent)] overflow-hidden"
                onToggle={(e) => {
                  const el = e.currentTarget as HTMLDetailsElement;
                  if (e.isTrusted && el.open) trackAccordion("who", true);
                }}
              >
                <summary className="cursor-pointer select-none list-none px-5 py-4 font-semibold text-[var(--color-text-brand)] flex items-center justify-between">
                  Is this right for me?
                  <span className="transition-transform group-open:rotate-180">▾</span>
                </summary>
                <div className="px-5 pb-5 text-gray-700 leading-relaxed">
                  <ul className="list-disc pl-5 space-y-1">
                    {(Array.isArray(product.who)
                      ? product.who
                      : product.who.split(",").map((s) => s.trim())
                    ).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </details>
            )}

            {product.directions && (
              <details
                open
                className="group bg-[var(--color-bg-muted)] rounded-xl border border-[var(--color-accent)] overflow-hidden"
                onToggle={(e) => {
                  const el = e.currentTarget as HTMLDetailsElement;
                  if (e.isTrusted && el.open) trackAccordion("how_to_use", true);
                }}
              >
                <summary className="cursor-pointer select-none list-none px-5 py-4 font-semibold text-[var(--color-text-brand)] flex items-center justify-between">
                  How to use
                  <span className="transition-transform group-open:rotate-180">▾</span>
                </summary>
                <div className="px-5 pb-5 text-gray-700 leading-relaxed space-y-2">
                  {(Array.isArray(product.directions)
                    ? product.directions
                    : [product.directions]
                  ).map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
              </details>
            )}

            {product.ingredients && (
              <details
                className="group bg-[var(--color-bg-muted)] rounded-xl border border-[var(--color-accent)] overflow-hidden"
                onToggle={(e) => {
                  const el = e.currentTarget as HTMLDetailsElement;
                  if (e.isTrusted && el.open) trackAccordion("ingredients", true);
                }}
              >
                <summary className="cursor-pointer select-none list-none px-5 py-4 font-semibold text-[var(--color-text-brand)] flex items-center justify-between">
                  Ingredients
                  <span className="transition-transform group-open:rotate-180">▾</span>
                </summary>
                <div className="px-5 pb-5 text-gray-700 leading-relaxed">
                  <ul className="list-disc pl-5 space-y-1">
                    {(Array.isArray(product.ingredients)
                      ? product.ingredients
                      : product.ingredients.split(",").map((s) => s.trim())
                    ).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </details>
            )}

            {product.faq && product.faq.length > 0 && (
              <div className="faq-section">
                <h2 className="text-2xl font-bold text-[var(--color-text-brand)] mb-6">FAQ</h2>
                {product.faq.map((faqItem, index) => (
                  <details
                    key={index}
                    className="group bg-[var(--color-bg-muted)] rounded-xl border border-[var(--color-accent)] overflow-hidden mb-4"
                    onToggle={(e) => {
                      const el = e.currentTarget as HTMLDetailsElement;
                      if (e.isTrusted && el.open) trackAccordion(`faq_${index}`, true);
                    }}
                  >
                    <summary className="cursor-pointer select-none list-none px-5 py-4 font-semibold text-[var(--color-text-brand)] flex items-center justify-between">
                      {faqItem.question}
                      <span className="transition-transform group-open:rotate-180">▾</span>
                    </summary>
                    <div className="px-5 pb-5 text-gray-700 leading-relaxed">
                      {faqItem.answer}
                    </div>
                  </details>
                ))}
              </div>
            )}

            {product.blog && (
              <div className="bg-[var(--color-bg-muted)] rounded-xl border border-[var(--color-accent)] px-5 py-4 flex items-center justify-between">
                <span className="text-sm font-medium text-[var(--color-text-secondary)]">Want to learn more?</span>
                <TrackedLink
                  href={product.blog}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-brand)] border border-[var(--color-brand)] rounded-full px-4 py-1.5 hover:bg-[var(--color-brand)] hover:text-white transition-colors"
                  event="blog_link_click"
                  label="Read our full guide"
                  component="ProductPage"
                >
                  Read our full guide →
                </TrackedLink>
              </div>
            )}
          </div>

          {/* Secure checkout + payment logos */}
          <div className="mt-5">
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
              <Lock className="w-4 h-4 text-[var(--color-text-secondary)]" aria-hidden />
              <span>Secure checkout</span>
            </div>
            <div className="mt-2">
              <PaymentsRow />
            </div>
          </div>
        </div>
      </div>

      {/* Sticky mobile buy bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--color-bg-muted)]/90 backdrop-blur supports-[backdrop-filter]:bg-[var(--color-bg-muted)]/70 border-t border-gray-200">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-start gap-3 pr-24">
          <AddToCart product={product} useStepper layout="compact" />
          <div className="ml-auto text-right">
            <p className="text-base font-semibold text-gray-900">{product.price}</p>
            <p className="text-[11px] text-[var(--color-text-secondary)] flex items-center justify-end gap-1">
              <Lock className="w-3 h-3 text-[var(--color-text-secondary)]" aria-hidden />
              Secure checkout
            </p>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-4 pb-3 pr-24">
          <PaymentsRow compact />
        </div>
      </div>

      {/* JSON-LD for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.title,
            image: [product.imageSrc],
            description: product.description ?? product.title,
            sku: product.id,
            brand: schemaBrand(),
            offers: {
              "@type": "Offer",
              priceCurrency: "GBP",
              ...(!isNaN(numericPrice) ? { price: numericPrice } : {}),
              availability: "https://schema.org/InStock",
              url: `https://cforganicwellness.com/shop/${product.slug}`,
              ...schemaOfferPolicies(isNaN(numericPrice) ? null : numericPrice),
            },
            ...(hasRating
              ? {
                  aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: product.rating,
                    reviewCount: product.reviewCount,
                  },
                }
              : {}),
          }),
        }}
      />

      {/* FAQPage JSON-LD. The nutrition detail pages emit this; product pages did
          not, despite rendering the same visible Q&A. Google forbids the markup for
          answers a visitor cannot see — these are all visible, so if the FAQ section
          is ever collapsed away or hidden, drop this with it. */}
      {product.faq && product.faq.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: product.faq.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
              })),
            }),
          }}
        />
      )}
    </section>
  );
};

export default ProductDetailClient;
